import { useRef, useEffect } from 'react';
import { ChevronRight, Sparkles, X } from 'lucide-react';
import { Avatar } from '../../../components/ui/Avatar.jsx';
import { YogiVideoLinks } from './YogiVideoLinks.jsx';
import { YogiReminderPicker } from './YogiReminderPicker.jsx';

/**
 * @param {{
 *   message: import('../yogiTypes.js').YogiMessage,
 *   profilePictureUrl?: string | null,
 *   userName?: string,
 *   index: number,
 * }} props
 */
function YogiMessage({ message, profilePictureUrl, userName, index }) {
  const isBot = message.role === 'bot';

  return (
    <div
      className={`flex gap-2.5 yogi-message-in ${isBot ? '' : 'flex-row-reverse'}`}
      style={{ animationDelay: `${Math.min(index * 40, 200)}ms` }}
    >
      {isBot ? (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent-peach/40 to-white ring-2 ring-white shadow-sm">
          <img src="/images/Yogi.png" alt="" className="h-7 w-7 object-contain" />
        </div>
      ) : (
        <Avatar
          src={profilePictureUrl}
          name={userName || ''}
          size="sm"
          className="h-9 w-9 self-end"
        />
      )}

      <div
        className={`max-w-[82%] whitespace-pre-line px-4 py-3 text-[15px] leading-relaxed shadow-[var(--shadow-sm)] ${
          isBot
            ? 'rounded-[var(--radius-md)] rounded-tl-sm border border-brand/8 bg-white text-text'
            : 'rounded-[var(--radius-md)] rounded-tr-sm bg-gradient-to-br from-brand to-brand-light text-white'
        }`}
      >
        {message.text}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   messages: import('../yogiTypes.js').YogiMessage[],
 *   pendingOptions: import('../yogiTypes.js').YogiOption[] | null,
 *   activeComponent: import('../yogiTypes.js').YogiComponentStep | null,
 *   context: import('../yogiTypes.js').YogiContext,
 *   profilePictureUrl?: string | null,
 *   userId?: string,
 *   userName?: string,
 *   phoneNumber?: string,
 *   onOptionSelect: (option: import('../yogiTypes.js').YogiOption) => void,
 *   onComponentComplete: (value?: string) => void,
 *   onClose: () => void,
 * }} props
 */
export function YogiChatPanel({
  messages,
  pendingOptions,
  activeComponent,
  context,
  profilePictureUrl,
  userId,
  userName,
  phoneNumber,
  onOptionSelect,
  onComponentComplete,
  onClose,
}) {
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pendingOptions, activeComponent]);

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-30 bg-brand/20 backdrop-blur-[3px] animate-[fade-in_0.2s_ease-out] md:bg-brand/10 md:backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close Yogi chat"
      />

      <div
        className="fixed inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-40 flex max-h-[min(540px,calc(100vh-7rem))] flex-col overflow-hidden rounded-[var(--radius-xl)] border border-brand/10 bg-white shadow-[var(--shadow-lg)] modal-slide-up md:inset-x-auto md:bottom-auto md:right-6 md:top-28 md:h-[min(34rem,78vh)] md:w-[24rem] md:max-w-[calc(100vw-18rem)]"
        role="dialog"
        aria-labelledby="yogi-chat-title"
        aria-modal="true"
      >
        <header className="relative shrink-0 overflow-hidden bg-gradient-to-br from-brand via-brand-light to-brand px-4 py-4 text-white">
          <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-6 right-10 h-20 w-20 rounded-full bg-accent-peach/25" aria-hidden="true" />

          <div className="relative flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 p-1.5 ring-2 ring-white/25 backdrop-blur-sm">
                <img src="/images/Yogi.png" alt="" className="h-full w-full object-contain" />
              </div>
              <span
                className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-brand bg-success"
                aria-hidden="true"
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h2 id="yogi-chat-title" className="truncate text-lg font-semibold tracking-tight">
                  Yogi
                </h2>
                <Sparkles className="h-4 w-4 shrink-0 text-accent-peach/90" strokeWidth={2} aria-hidden="true" />
              </div>
              <p className="text-xs text-white/75">Your personal wellness guide</p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 transition-colors hover:bg-white/25 min-h-0 min-w-0"
              aria-label="Close Yogi"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </header>

        <div
          ref={scrollRef}
          className="flex-1 space-y-4 overflow-y-auto overscroll-contain bg-gradient-to-b from-surface/60 via-white to-surface-card/30 px-4 py-4"
        >
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-peach/25">
                <img src="/images/Yogi.png" alt="" className="h-12 w-12 object-contain yogi-float" />
              </div>
              <p className="max-w-[220px] text-sm text-text-secondary">
                Hi there! I&apos;m here to help you feel your best.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <YogiMessage
              key={`${msg.role}-${i}`}
              message={msg}
              profilePictureUrl={profilePictureUrl}
              userName={userName}
              index={i}
            />
          ))}

          {activeComponent?.component === 'videoLinks' && (
            <div className="yogi-message-in rounded-[var(--radius-md)] border border-brand/8 bg-white p-3 shadow-[var(--shadow-sm)]">
              <YogiVideoLinks onComplete={() => onComponentComplete()} />
            </div>
          )}

          {activeComponent?.component === 'reminderPicker' && userId && (
            <div className="yogi-message-in">
              <YogiReminderPicker
                userId={userId}
                name={userName}
                phoneNumber={phoneNumber}
                symptom={context.symptom}
                onComplete={(value) => onComponentComplete(value)}
              />
            </div>
          )}
        </div>

        {pendingOptions && pendingOptions.length > 0 && (
          <div className="shrink-0 max-h-52 overflow-y-auto border-t border-brand/8 bg-surface-card/90 p-3 backdrop-blur-sm">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
              Quick replies
            </p>
            <div className="space-y-2">
              {pendingOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onOptionSelect(option)}
                  className="group flex w-full items-center justify-between gap-3 rounded-xl border border-brand/10 bg-white px-4 py-3 text-left text-sm font-medium text-brand shadow-[var(--shadow-sm)] transition-all hover:border-brand/20 hover:bg-brand/[0.04] hover:shadow-[var(--shadow-md)] active:scale-[0.99] min-h-[44px]"
                >
                  <span className="leading-snug">{option.label}</span>
                  <ChevronRight
                    className="h-4 w-4 shrink-0 text-text-secondary transition-transform group-hover:translate-x-0.5 group-hover:text-brand"
                    strokeWidth={2}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
